import React from "react";
import { Form, Row, Col, Tooltip, OverlayTrigger } from "react-bootstrap";
import { Card, CardBody } from "../../../_metronic/_partials/controls";
import classNames from "classnames";
import { Controller, useFormContext } from "react-hook-form";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { useDatePickerFormat } from "../../../helpers/dateFormat";
import "react-datepicker/dist/react-datepicker.css";
import { ErrorMessage } from "@hookform/error-message";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
  getTransactions,
  getTransactionsCSVList,
} from "../../../store/actions";
import "./SearchTransaction.scss";

const SearchTransaction = (props) => {
  const {
    register,
    control,
    formState: { errors },
    reset,
  } = useFormContext({ reValidateMode: "onChange" });
  const intl = useIntl();
  const dispatch = useDispatch();
  const { taxonomy } = useSelector((state) => state?.taxonomy);
  const datePickerFormat = useDatePickerFormat();

  const statusFilterList = [
    {
      value: "PENDING",
      label: intl.formatMessage({ id: "transaction.search.pending" }),
    },
    {
      value: "SUCCESS",
      label: intl.formatMessage({ id: "transaction.search.success" }),
    },
    {
      value: "FAIL",
      label: intl.formatMessage({ id: "transaction.search.fail" }),
    },
    {
      value: "REFUNDED",
      label: intl.formatMessage({ id: "transaction.search.refunded" }),
    },
  ];

  const clearSearchData = () => {
    dispatch(getTransactionsCSVList(""));
    props.clearData(null);
    reset();
  };

  return (
    <>
      <Card>
        <CardBody className="px-4 py-6">
          <Row className="justify-content-md-center">
            <h3 className="page-heading-text gap-1 ps-4 mb-5">
              Search
              <OverlayTrigger
                overlay={
                  <Tooltip id="search-transaction-tooltip">
                    Use one or more filters above to refine your search results.
                    You can combine multiple criteria for more accurate results.
                  </Tooltip>
                }>
                <span className="tooltip-wrapper">
                  <i className="fa fa-question-circle" />
                </span>
              </OverlayTrigger>
            </h3>
            <Col md={12}>
              <Row className="form-group gap-5 gap-md-0 mb-5">
                <Col md={4}>
                  <Form.Control
                    name="campaign"
                    placeholder={`Search by ${taxonomy?.projectSingular}`}
                    className={classNames("form-control common-search-input")}
                    {...register("campaign")}
                  />
                </Col>
                <Col md={4}>
                  <Form.Control
                    name="investor"
                    placeholder="Search by Investor"
                    className={classNames("form-control common-search-input")}
                    {...register("investor")}
                  />
                </Col>
                <Col md={4}>
                  <Form.Control
                    name="transaction"
                    placeholder="Search by Transaction ID"
                    className={classNames("form-control common-search-input")}
                    {...register("transaction")}
                  />
                </Col>
              </Row>
              <Row className="form-group gap-5 gap-md-0 mb-5">
                <Col md={4}>
                  <Form.Control
                    name="minamount"
                    placeholder="Search by Min Amount"
                    className={classNames("form-control common-search-input", {
                      "is-invalid": errors.minamount,
                    })}
                    {...register("minamount", {
                      pattern: {
                        value: /[0-9]/,
                        message: "Minimum amount should be positive number",
                      },
                    })}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="minamount"
                    render={({ message }) => (
                      <div className="invalid-feedback">{message}</div>
                    )}
                  />
                </Col>
                <Col md={4}>
                  <Form.Control
                    name="maxamount"
                    placeholder="Search by Max Amount"
                    className={classNames("form-control common-search-input", {
                      "is-invalid": errors.maxamount,
                    })}
                    {...register("maxamount", {
                      pattern: {
                        value: /[0-9]/,
                        message: "Maximum amount should be positive number",
                      },
                    })}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="maxamount"
                    render={({ message }) => (
                      <div className="invalid-feedback">{message}</div>
                    )}
                  />
                </Col>
                <Col md={4}>
                  <Controller
                    name="preapprovalStatus"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        classNamePrefix="filter-select"
                        placeholder={intl.formatMessage({
                          id: "transaction.search.status",
                        })}
                        options={statusFilterList}
                        value={statusFilterList.find(
                          (option) => option.value === field.value
                        )}
                        onChange={(selected) =>
                          field.onChange(selected ? selected.value : "")
                        }
                      />
                    )}
                  />
                </Col>
              </Row>
              <Row className="form-group gap-5 gap-md-0 mb-5">
                <Col md={4} className="form-group transaction-filter-wrapper">
                  <Controller
                    name="startDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        className="form-control py-1-5"
                        placeholderText="Start Date"
                        selected={field.value}
                        onChange={(date) => field.onChange(date)}
                        dateFormat={datePickerFormat}
                        isClearable
                      />
                    )}
                  />
                </Col>
                <Col md={4} className="form-group transaction-filter-wrapper">
                  <Controller
                    name="endDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        className="form-control py-1-5"
                        placeholderText="End Date"
                        selected={field.value}
                        onChange={(date) => field.onChange(date)}
                        dateFormat={datePickerFormat}
                        isClearable
                      />
                    )}
                  />
                </Col>
                <Col md={4}>
                  <Controller
                    name="byDays"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        classNamePrefix="filter-select"
                        placeholder="Filter By Days"
                        options={[
                          { value: 7, label: "Last 7 Days" },
                          { value: 14, label: "Last 14 Days" },
                          { value: 21, label: "Last 21 Days" },
                          { value: 28, label: "Last 28 Days" },
                        ]}
                        value={
                          field.value
                            ? {
                                value: field.value,
                                label: `Last ${field.value} Days`,
                              }
                            : null
                        }
                        onChange={(selected) =>
                          field.onChange(selected ? selected.value : "")
                        }
                        isClearable
                      />
                    )}
                  />
                </Col>
              </Row>
              <div className="form-group gap-2 d-flex">
                <div>
                  <button className="common-search-btn search-reset-btn">
                    Search
                  </button>
                </div>
                <div>
                  <button
                    className="btn text-black py-2 search-reset-btn ms-3 font-size-sm fw-medium bg-select-bg-clr"
                    type="button"
                    onClick={() => clearSearchData()}>
                    Reset
                  </button>
                </div>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>
    </>
  );
};

export default SearchTransaction;
